/**
 * useSendQuery — the chat send pipeline (WS-only since Jun 26, 2026).
 *
 * Behavior:
 *   1. Append optimistic user-turn message (sub-50ms UX target).
 *   2. WSClient is the SINGLE transport.
 *      a. ``connected``        → emit the WSSendQuery envelope.
 *      b. ``closed``/``reconnecting`` → ``ws.ensureConnected({timeoutMs})``
 *         resets the attempt counter (BE-restart + idle-tab scenario
 *         exhausts the auto-reconnect budget) and waits up to 3s for the
 *         open. On success → emit envelope.
 *      c. Still disconnected after timeout → append a system error
 *         message with a clear recovery hint. There is NO REST
 *         fallback any more — the Phase 5 ``/chat/send`` placeholder
 *         was NEVER implemented BE-side, every fallback returned 404
 *         and silently masked the disconnect (Jun 26 2026 incident).
 *
 * Single-transport contract is intentionally strict: the previous
 * "either WS or REST" branch hid the real failure mode under a generic
 * "send failed" message; the new contract surfaces "Connessione
 * interrotta. Ricarica l'app." so the user has agency.
 *
 * Streaming appends ride the existing ``attachChatHandlers`` wire
 * (Phase 6.A.4) — this hook returns immediately, the answer lands
 * asynchronously as ``text`` / ``answer`` / ``complete`` events.
 *
 * v1 ref: ``SocketContext.sendQuery()`` (lines 770-879).
 */

import { useLingui } from "@lingui/react/macro";

import type { WSSendQuery } from "@ando/core/ws";
import type {
  ChatFileAttachment,
  ChatImageAttachment,
} from "@ando/core/domain";
import { useCallback, useMemo } from "react";

import {
  getActiveContextSnapshot,
  isActiveContextDismissed,
} from "@/features/active-context";
import { useProjectsStore } from "@/features/projects/state/projectsStore.js";
import { useWorkspaceRouter } from "@/features/workspace-router";

import { useWsClient } from "@/features/api";
import {
  selectCurrentConversationId,
  selectIsStreamingFor,
  useChatStore,
} from "../state/chatStore.js";
import type { ConversationConfig } from "../state/types.js";
import { normalizeComposerFocus } from "../lib/composerFocus.js";
import { useChatUserId } from "./useChatSession.js";
import { useCreateConversation } from "./useConversationMutations.js";

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `msg-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

/**
 * Inline ``@``-mention payload carried alongside the prompt text.
 *
 * Mirrors the wire shape on ``WSSendQuery.mentions`` — keeping the type
 * here lets the composer pass strongly-typed objects without importing
 * ``@ando/core/api`` (which would coupling the chat slice to the
 * mention-search endpoint).
 */
export interface SendQueryMention {
  kind: "document" | "person" | "folder" | "connector" | "thread";
  id: string;
  display: string;
}

/** Wire-ready CAD spatial filter — mirrors ``WSSendQuery.spatial_filter``. */
export type SpatialFilter = NonNullable<WSSendQuery["spatial_filter"]>;

/** Wire-ready CAD feature filter — Phase 5 of the CAD Big Bang.
 *
 * Mirrors the BE ``_validate_feature_filter`` schema. Loose typing so
 * the FE doesn't need to mirror every BE-side enum (the BE re-validates
 * + drops unknown keys); the WS schema may not yet declare it.
 */
export interface FeatureFilter {
  feature_type?: string[];
  feature_subtype?: string[];
  diameter_mm_min?: number;
  diameter_mm_max?: number;
  material_name?: string[];
  format?: string[];
  source_file?: string;
}

export interface SendQueryInput {
  text: string;
  /** Opaque signed context; never appended to the visible chat message. */
  businessDefinitionToken?: string;
  /** Conversation to send to — defaults to `currentId`. */
  conversationId?: string;
  /** Override per-call config (e.g. force expert mode for this send). */
  configOverride?: Partial<ConversationConfig>;
  /**
   * Inline ``@``-mentions resolved by the composer autocomplete.
   *
   * The BE may consume these to scope retrieval or to enrich audit logs.
   * Empty / undefined → field is dropped from the envelope (graceful for
   * agents that don't yet read it).
   */
  mentions?: readonly SendQueryMention[];
  /**
   * CAD spatial filter — a DXF-world rectangle (``{min_x,…,source_file}``)
   * resolved by the Analisi CAD modal. Dropped from the envelope when absent.
   */
  spatialFilter?: SpatialFilter;
  /**
   * CAD feature filter — multi-select (feature type / subtype / diameter
   * range / material) resolved by the Analisi CAD modal "Filtro feature"
   * tab. Forwarded as ``WSSendQuery.feature_filter`` (loose-typed wire
   * field; BE owns the canonical validation in
   * ``_validate_feature_filter``).
   */
  featureFilter?: FeatureFilter;
  /**
   * Inline image attachments uploaded via ``uploadChatImage`` (vision).
   * Forwarded verbatim to ``WSSendQuery.image_attachments`` so the BE can
   * resolve MinIO blobs at agent runtime + mirrored on the optimistic
   * user-message append for instant history render. Dropped when empty.
   */
  imageAttachments?: ReadonlyArray<ChatImageAttachment>;
  /**
   * Inline file attachments uploaded via ``uploadChatFile`` (PDF / docx /
   * voice-note WebM). Forwarded to ``WSSendQuery.file_attachments`` +
   * mirrored on the user message. The voice-note variant carries
   * ``mime_type: audio/*`` — the BE may run STT (Whisper) over it
   * before passing to the LLM; the user-side ``<audio controls>``
   * player in chat history reads the same ``content_id``.
   */
  fileAttachments?: ReadonlyArray<ChatFileAttachment>;
  /** Test seam. */
  now?: () => number;
}

export interface SendQueryResult {
  /** Synthetic query id we send to the BE; echoed back via the answer event. */
  queryId: string;
  /** Transport actually used. Always ``"ws"`` since Jun 26, 2026 — kept
   *  as a literal for call-site compatibility (telemetry, tests). */
  transport: "ws";
}

export interface UseSendQueryResult {
  send: (input: SendQueryInput) => Promise<SendQueryResult>;
  pending: boolean;
}

export function useSendQuery(): UseSendQueryResult {
  const userId = useChatUserId();
  const ws = useWsClient();
  const { t } = useLingui();
  const { navigateToDecision } = useWorkspaceRouter();

  const appendMessageTo = useChatStore((s) => s.appendMessageTo);
  const setStreamingFor = useChatStore((s) => s.setStreamingFor);
  const selectConversation = useChatStore((s) => s.selectConversation);
  const currentId = useChatStore(selectCurrentConversationId);
  const conversations = useChatStore((s) => s.conversations);
  const pending = useChatStore(selectIsStreamingFor(currentId));
  // Reuse the same conversation-bootstrap path the "Nuova chat" button
  // uses (``upsertConversation`` → enters ``orderedIds`` → sidebar sees
  // it). Pre-June-3 fallback used the magic string ``"draft"`` here,
  // which slipped into ``messagesByConversation["draft"]`` but never
  // ``orderedIds`` — the message was sent + answered, yet the
  // conversation never appeared in the sidebar (user-reported "limbo"
  // after composing in chat from the document-draft route).
  const { create: createConversation } = useCreateConversation();

  const send = useCallback(
    async (input: SendQueryInput): Promise<SendQueryResult> => {
      const text = input.text.trim();
      // June 3, 2026 — accept attachment-only turns (voice-note send
      // without typed text). Reject only when the turn carries
      // literally nothing — no text AND no attachments. Caller
      // (useComposerSubmit) already enforces the same rule but this
      // is the authoritative gate; keeping it in sync prevents a
      // silent throw → swallowed-catch → empty WS frame regression.
      const hasImages = (input.imageAttachments?.length ?? 0) > 0;
      const hasFiles = (input.fileAttachments?.length ?? 0) > 0;
      if (text.length === 0 && !hasImages && !hasFiles) {
        throw new Error("useSendQuery: empty turn (no text, no attachments)");
      }

      // June 3 2026 — when nothing identifies a conversation (no caller
      // override, no ``currentId``), bootstrap a fresh one through the
      // SAME ``useCreateConversation`` path the "Nuova chat" button
      // uses. That registers the conversation in ``conversations`` AND
      // ``orderedIds`` AND inherits the active project so the sidebar
      // immediately shows it. The pre-fix fallback ``"draft"`` only
      // populated ``messagesByConversation``, so the chat answered
      // correctly but the sidebar never listed the conversation — the
      // "limbo" state the user hit when typing into the chat from the
      // /workspace/documents draft route.
      const conversationId =
        input.conversationId ?? currentId ?? createConversation();
      // Activate the target conversation as current so the chat pane
      // (which selects messages via ``s.currentId``) renders the new
      // user/agent turns. ``createConversation()`` already selects, but
      // we still need this branch for the explicit ``input.conversationId``
      // path (e.g. retry from a different conversation).
      if (currentId !== conversationId) {
        selectConversation(conversationId);
      }
      const now = input.now ?? Date.now;
      const queryId = newId();

      // Resolve effective config: per-conversation + per-call overrides.
      const persisted = conversations[conversationId]?.config;
      const baseConfig: ConversationConfig = {
        mode: persisted?.mode ?? "fast",
        webSearchMode: persisted?.webSearchMode ?? false,
        focus: persisted?.focus ?? "standard",
        selectedConnectionIds: persisted?.selectedConnectionIds ?? [],
        agentModeVersionId: persisted?.agentModeVersionId ?? null,
        folderFilter: persisted?.folderFilter ?? [],
        scopedDocuments: persisted?.scopedDocuments ?? [],
        // June 14, 2026 — Ando Connect "interroga un'area" scope (read-only).
        connectSpace: persisted?.connectSpace ?? null,
        // Jul 2026 — TERRA β experimental retrieval engine (admin toggle).
        terraMode: persisted?.terraMode ?? false,
        tramaInfrastructure: persisted?.tramaInfrastructure,
      };
      const config: ConversationConfig = {
        ...baseConfig,
        focus: normalizeComposerFocus(baseConfig),
        // Other surfaces can still explicitly request the existing full contract.
        ...input.configOverride,
      };

      // June 2026 — merge the sticky per-conversation document scope
      // (``config.scopedDocuments``, picked through SourceScopePicker)
      // into the inline ``mentions[]`` array. Both arrive at the BE
      // ``build_mention_filters`` through the SAME wire field — no new
      // contract needed. Dedup by ``id`` keeps an inline ``@``-mention
      // and a sticky pick of the same file from double-firing.
      const inlineMentions = input.mentions ?? [];
      const stickyMentions: SendQueryMention[] = config.scopedDocuments.map((d) => ({
        kind: "document" as const,
        id: d.id,
        display: d.display,
      }));
      const mergedMentions: SendQueryMention[] = (() => {
        if (stickyMentions.length === 0) return [...inlineMentions];
        const seen = new Set<string>();
        const out: SendQueryMention[] = [];
        for (const m of inlineMentions) {
          const key = `${m.kind}:${m.id}`;
          if (seen.has(key)) continue;
          seen.add(key);
          out.push(m);
        }
        for (const m of stickyMentions) {
          const key = `${m.kind}:${m.id}`;
          if (seen.has(key)) continue;
          seen.add(key);
          out.push(m);
        }
        return out;
      })();

      // 1. Optimistic user-turn append. Attachments AND scope mentions
      // are mirrored on the local message so the history shows what
      // the user saw at send time (no round-trip wait for rebuild).
      // Scope mentions are persisted with their full enrichment
      // (provider + folderPath) so the chip renderer can build a rich
      // muted-secondary line without re-fetching from mention search.
      const messageMentions =
        mergedMentions.length > 0
          ? mergedMentions.map((m) => {
              // Sticky-scope docs carry richer fields than inline
              // ``@``-mentions — find the matching ``ScopedDocument``
              // in config and merge its provider/folderPath onto the
              // persisted record. Inline ``@``-mentions stay minimal.
              const sticky = config.scopedDocuments.find(
                (d) => d.id === m.id,
              );
              return {
                kind: m.kind,
                id: m.id,
                display: m.display,
                ...(sticky?.provider ? { provider: sticky.provider } : {}),
                ...(sticky?.folderPath
                  ? { folderPath: sticky.folderPath }
                  : {}),
              };
            })
          : undefined;
      appendMessageTo(conversationId, {
        id: newId(),
        conversationId,
        queryId,
        role: "user",
        content: text,
        timestamp: new Date(now()).toISOString(),
        status: "sent",
        ...(input.imageAttachments && input.imageAttachments.length > 0
          ? { image_attachments: [...input.imageAttachments] }
          : {}),
        ...(input.fileAttachments && input.fileAttachments.length > 0
          ? { file_attachments: [...input.fileAttachments] }
          : {}),
        ...(messageMentions ? { mentions: messageMentions } : {}),
      });
      setStreamingFor(conversationId, {
        queryId,
        isStreaming: true,
        status: undefined,
      });

      // 2. WS-first — with active re-connect when the socket is in a
      //    "closed" / "reconnecting" terminal state (e.g. backend
      //    restart exhausted the auto-reconnect attempt budget while
      //    the tab was idle). ``ensureConnected`` resets the
      //    attempts counter for THIS user-initiated send and races
      //    the open against a short timeout, so a transient drop is
      //    recovered transparently. If the deadline elapses we fall
      //    through to the error branch below — there is NO REST
      //    fallback any more (the previous ``/chat/send`` placeholder
      //    was never implemented BE-side, every fallback returned
      //    404 silently).
      if (userId !== null) {
        if (ws.status !== "connected") {
          try {
            await ws.ensureConnected({ timeoutMs: 3_000 });
          } catch {
            // Fall through to the error branch below; no REST
            // fallback to mask the failure.
          }
        }
      }
      if (ws.status === "connected" && userId !== null) {
        // Project scope (May 2026) — snapshot the active project id at send
        // time. Using ``getState()`` keeps the hook out of the project store's
        // subscription graph (no re-render churn when the pill flips).
        const currentProjectId = useProjectsStore.getState().currentProjectId;
        // Active context (Jun 13, 2026) — what entity the user has OPEN
        // in an inline viewer / source drawer at send time (calendar
        // event, doc preview, inbox thread, citation pin-point). The BE
        // formats this into the system prompt so the agent grounds its
        // answer in the artifact the user is looking at without the
        // user having to spell it out. Snapshot-read for the same
        // no-re-render-churn reason as the project id.
        const activeContextRaw = getActiveContextSnapshot();

        // Jun 17, 2026 — explicit > implicit precedence, with
        // convergent-scope reinforcement.
        //
        // Three states:
        //   - DIVERGENT: explicit scope set, open sidebar viewer lives
        //     OUTSIDE that scope → drop active_context (user moved
        //     scope away from the open viewer; explicit wins).
        //   - CONVERGENT: explicit scope set, open sidebar viewer
        //     lives INSIDE that scope (intersection of
        //     ``active.folderIds`` with ``folderFilter`` non-empty) →
        //     keep BOTH. The folder bounds the search, the sidebar
        //     entity gives precise grounding within those bounds —
        //     they reinforce.
        //   - IMPLICIT-ONLY: no explicit scope → use active_context
        //     as the implicit grounding (current pre-fix behaviour).
        //
        // Mentions + connect space + sticky docs are always "strong
        // explicit" — they're specific entities the user has tagged,
        // so a sidebar viewer in addition to them is treated as
        // divergent unless we have evidence otherwise. We model that
        // as "convergence is supported ONLY when the explicit signal
        // is a folder scope" — anything else drops the sidebar.
        const hasStrongExplicit =
          mergedMentions.length > 0 || config.connectSpace?.id != null;
        const hasExplicitFolder = config.folderFilter.length > 0;
        const folderIntersects =
          hasExplicitFolder &&
          (activeContextRaw?.folderIds?.some((id) =>
            config.folderFilter.includes(id),
          ) ?? false);
        // Jul 13, 2026 — the ambient context chip's ✕ suppresses the open
        // entity by explicit user choice ("don't use the thing I have
        // open"). This is the SAME drop as the explicit-scope override,
        // just triggered by the user instead of by their scope, so it
        // rides the identical predicate here — one drop-logic, no drift
        // between wire and the chip's visibility (which reads the same
        // ``isDismissed`` flag via ``useActiveContextOverride``).
        const isDismissedContext = isActiveContextDismissed();
        const didOverrideContext =
          activeContextRaw !== null &&
          (isDismissedContext ||
            hasStrongExplicit ||
            (hasExplicitFolder && !folderIntersects));
        const activeContext = didOverrideContext ? null : activeContextRaw;
        const envelope: WSSendQuery = {
          user_id: userId,
          query: text,
          ...(input.businessDefinitionToken
            ? { business_definition_token: input.businessDefinitionToken }
            : {}),
          query_id: queryId,
          conversation_id: conversationId,
          // ``collection_names`` is the Weaviate vector-collection scope for
          // RAG; the structured-source picker (DB / OB / spreadsheet / CRM
          // / …) writes into a SEPARATE list that the BE reads as
          // ``connection_ids`` (see ``websocket_pydantic.py::data.get
          // ("connection_ids")``). The composer state stores both under the
          // same ``selectedConnectionIds`` field because the picker is
          // single-purpose today — sourcing structured enterprise data.
          // Jun 4 2026 bug: this envelope mistakenly aliased the
          // structured ids into ``collection_names`` AND left
          // ``connection_ids`` empty, so the BE saw zero scope and answered
          // from the full RAG index (the user got fornitori/orders from
          // ERP when they had Open Banking selected). Sending it on the
          // correct field restores the picker's intent — and keeps
          // ``collection_names`` available for a future RAG scope chip.
          collection_names: [],
          connection_ids: config.selectedConnectionIds,
          folder_filter: config.folderFilter,
          // May 26, 2026 — two-tier mode selector. Replaces the legacy
          // ``search_power`` + ``thinking_mode`` pair on the wire (BE
          // still accepts both for backward compat via
          // ``normalize_search_power``).
          mode: config.mode,
          web_search_mode: config.webSearchMode,
          focus: config.focus,
          agent_mode_version_id: config.agentModeVersionId,
          project_id: currentProjectId,
          // June 14, 2026 — Ando Connect "interroga un'area" scope. When set,
          // the BE re-validates membership and answers ONLY from this Space's
          // granted, read-only slice. Omitted in normal chat so the wire shape
          // is unchanged for everyone not querying a shared area.
          ...(config.connectSpace?.id
            ? { connect_space_id: config.connectSpace.id }
            : {}),
          // Jul 2026 — TERRA β. Stamped ONLY when the admin toggle is ON
          // so every other send keeps the existing wire shape; the BE
          // re-verifies the platform-admin claim before honouring it.
          ...(config.terraMode ? { retrieval_engine: "trama" as const } : {}),
          ...(config.tramaInfrastructure
            ? { retrieval_engine: "trama" as const, trama_infrastructure: config.tramaInfrastructure }
            : {}),
          // Inline ``@``-mentions + sticky per-conversation document
          // scope (``config.scopedDocuments``) merged + deduped above.
          // Field is omitted when empty so BE agents that don't yet read
          // it stay on the existing wire-format.
          ...(mergedMentions.length > 0
            ? {
                mentions: mergedMentions.map((m) => ({
                  kind: m.kind,
                  id: m.id,
                  display: m.display,
                })),
              }
            : {}),
          // CAD spatial filter — omitted when absent so non-CAD sends keep
          // the existing wire shape.
          ...(input.spatialFilter ? { spatial_filter: input.spatialFilter } : {}),
          // Phase 5 (Jun 12, 2026 SOTA — CAD Big Bang) — CAD feature
          // multi-select filter. Loose-typed cast so the FE doesn't have
          // to mirror the schema in two places; BE owns the canonical
          // validation in ``_validate_feature_filter``.
          ...(input.featureFilter
            ? { feature_filter: input.featureFilter as never }
            : {}),
          // Image + file attachments (June 3, 2026 — voice-note loop):
          // pass content_id / minio_key refs so the BE can resolve the
          // MinIO blob at agent runtime + persist the same ref on the
          // user message row (so history rebuild surfaces the same
          // ``<audio controls>`` / file chip). Dropped when empty to
          // keep the envelope minimal.
          ...(input.imageAttachments && input.imageAttachments.length > 0
            ? { image_attachments: [...input.imageAttachments] }
            : {}),
          ...(input.fileAttachments && input.fileAttachments.length > 0
            ? { file_attachments: [...input.fileAttachments] }
            : {}),
          // Active context — what entity the inline viewer / source
          // drawer has open at send time (see ``getActiveContextSnapshot``
          // import above). Omitted when ``null`` so plain chat sends
          // keep the existing wire shape; BE-side ``WSSendQuery`` schema
          // accepts the optional field.
          ...(activeContext ? { active_context: activeContext } : {}),
        };
        try {
          ws.send(envelope);
          return { queryId, transport: "ws" };
        } catch {
          // Fall through to REST — the WS send threw (socket may have
          // closed between status check and send).
        }
      }

      // 3. WS unavailable — surface the disconnect cleanly. The
      //    previous REST ``/chat/send`` fallback targeted a Phase 5
      //    placeholder endpoint that was NEVER implemented BE-side
      //    (Jun 26, 2026 incident: every fallback returned 404 and
      //    the user saw silent send failures). The single-transport
      //    contract (WS only) makes "no answer" impossible to mask:
      //    the error message tells the user exactly what to do.
      setStreamingFor(conversationId, { queryId: null, isStreaming: false });
      appendMessageTo(conversationId, {
        id: newId(),
        conversationId,
        queryId,
        role: "system",
        content: "",
        timestamp: new Date(now()).toISOString(),
        status: "error",
        systemLabel: t`Connessione interrotta. Controlla la rete o ricarica l'app.`,
      });
      throw new Error("WSClient: send aborted (socket not connected)");
    },
    [appendMessageTo, conversations, createConversation, currentId, navigateToDecision, selectConversation, setStreamingFor, t, userId, ws],
  );

  return useMemo(() => ({ send, pending }), [send, pending]);
}
